/** @format */

import {
  createFileRoute,
  Link,
  Outlet,
  redirect,
  useMatches,
} from "@tanstack/react-router";

export const Route = createFileRoute("/dev")({
  beforeLoad: ({ location }) => {
    if (import.meta.env.DEV) return;
    // `/dev/weather-radar` is intentionally available in production builds
    // so the docs screenshot capture can hit it on stage/prod. Every other
    // /dev/* route stays dev-only.
    if (location.pathname.startsWith("/dev/weather-radar")) return;
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect({ to: "/" });
  },
  component: DevLayout,
});

const DEV_PAGES = [
  {
    path: "/dev/error-boundaries",
    title: "Error Boundaries",
    description:
      "Full-page and inline error boundary variants — render errors, network errors, chunk errors, staff debug panel",
  },
  {
    path: "/dev/banners-toasts",
    title: "Banners & Toasts",
    description:
      "PWA install (Chromium, iOS, macOS Safari), sign-up banner, offline banner, update toasts (web + server, staff debug)",
  },
  {
    path: "/dev/notifications",
    title: "Notifications",
    description:
      "All notification categories, dropdown states (empty, loading, populated), push permission banners (prompt, blocked, error)",
  },
  {
    path: "/dev/revalidating-racer",
    title: "Revalidating Racer",
    description:
      "Stock car that laps the viewport edge while data revalidates — tune duration, size, margin, direction, color",
  },
  {
    path: "/dev/export-storyboard",
    title: "Export Storyboard",
    description:
      "Live preview of buildExportLayout — title, subtitle, logo, citation, axis labels, legend. Iterate without the download cycle.",
  },
  {
    path: "/dev/weather-radar",
    title: "Weather Radar",
    description:
      "Animated RainViewer precipitation tiles centered on Charlotte Motor Speedway. Preview the live-race-only widget without an active race.",
  },
] as const;

function DevLayout() {
  const matches = useMatches();
  const hasChildRoute = matches.some(
    match => match.routeId !== "/dev" && match.routeId.startsWith("/dev/")
  );

  if (hasChildRoute) {
    return <Outlet />;
  }

  return (
    <div className="max-w-3xl mx-auto p-6">
      <h1 className="text-2xl font-bold text-slate-100 mb-2">Dev Preview</h1>
      <p className="text-slate-400 mb-8 text-sm">
        Component preview pages for visual testing. Only available in development.
      </p>

      <div className="space-y-3">
        {DEV_PAGES.map(page => (
          <Link
            key={page.path}
            to={page.path}
            className="block rounded-lg border border-slate-700 bg-slate-900 p-4 hover:border-slate-500 hover:bg-slate-800 transition-colors"
          >
            <h2 className="text-base font-semibold text-slate-200">{page.title}</h2>
            <p className="text-sm text-slate-400 mt-1">{page.description}</p>
          </Link>
        ))}
      </div>
    </div>
  );
}
